import type { CSSProperties, ReactNode } from 'react';

import type { InlineSpan, RichBlock } from '@/lib/text/richMarkup';
import { INDENT_STEP, isDarkColor, parseRichBlocks } from '@/lib/text/richMarkup';

/**
 * 서식 자리표가 든 본문을 그린다 — 웹 쪽 렌더러 ({@link parseRichBlocks} 의 짝).
 *
 * <p>자리표가 하나도 없는 글은 <b>예전과 똑같이</b> 그려진다: 덩이 하나에 {@code pre-wrap}.
 * 그래서 지금까지 올라간 글의 모양이 달라지지 않는다.
 *
 * <p>문법과 색값은 여기 없다 — {@code richMarkup.ts} 한 곳에 있고 앱도 같은 것을 읽는다.
 */

/**
 * 본문 글자의 기본 꼴. 공지·지침·동향이 <b>다 같은 값</b>을 쓰고 있어서 기본값으로 굳힌다 —
 * 세 곳에 같은 style 객체를 적어 두면 한 곳만 고쳐지는 날이 온다.
 */
const BODY_STYLE: CSSProperties = {
  whiteSpace: 'pre-wrap',
  fontSize: '13px',
  lineHeight: '1.7',
  color: 'var(--text-secondary)',
};

export function RichText({
  text,
  style,
  className,
}: {
  text: string;
  /** 기본 꼴 위에 얹을 것. 글자 크기가 다른 자리(팝업·카드)에서만 쓴다. */
  style?: CSSProperties;
  className?: string;
}) {
  const blocks = parseRichBlocks(text);
  const bodyStyle = style ? { ...BODY_STYLE, ...style } : BODY_STYLE;

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {blocks.map((block, index) => (
        <BlockView key={index} block={block} bodyStyle={bodyStyle} />
      ))}
    </div>
  );
}

function BlockView({ block, bodyStyle }: { block: RichBlock; bodyStyle: CSSProperties }) {
  if (block.kind === 'divider') {
    // `hr` 이지 빈 칸이 아니다 — 위아래 여백을 넉넉히 줘야 「나눈다」 로 읽힌다.
    return <hr style={{ border: 0, borderTop: '1px solid var(--border-strong)', margin: '4px 0' }} />;
  }

  if (block.kind === 'table') {
    /*
      표는 <b>진짜 격자</b>로 그린다. `칸 | 칸` 을 글자 그대로 두면 긴 칸이 줄을 접는 순간
      다음 칸이 다른 줄로 밀려, 어느 값이 어느 항목의 것인지 읽을 수 없게 된다.

      좁은 화면에서는 가로로 민다(`overflow-x`). 칸을 세로로 쌓아 접으면 그건 표가 아니고,
      공지의 표는 대개 두세 칸이라 미는 편이 읽힌다.
    */
    return (
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            borderCollapse: 'collapse',
            ...bodyStyle,
            whiteSpace: 'normal',
            // 정렬을 준 표만 내용 폭으로 줄어든다 — 꽉 찬 표는 어디로 붙여도 자리가 같다.
            width: block.align ? 'auto' : '100%',
            marginLeft: block.align && block.align !== 'left' ? 'auto' : undefined,
            marginRight: block.align && block.align !== 'right' ? 'auto' : undefined,
            // 너비를 준 표만 고정 배치다 — 안 그러면 준 값이 글자 길이에 밀린다.
            tableLayout: block.widths?.length ? 'fixed' : undefined,
          }}
        >
          {block.widths?.length ? (
            <colgroup>
              {block.widths.map((width, index) => (
                <col key={index} style={{ width: `${width}%` }} />
              ))}
            </colgroup>
          ) : null}
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    style={{
                      border: '1px solid var(--border-strong)',
                      padding: '6px 10px',
                      verticalAlign: 'top',
                      wordBreak: 'keep-all',
                      // 칸의 색은 칸이 갖는다 — 글자에 그은 형광펜과 섞지 않는다.
                      background: cell.background,
                      textAlign: cell.align,
                    }}
                  >
                    <Spans spans={cell.spans} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <p
      style={{
        ...bodyStyle,
        textAlign: block.align,
        // 공백으로 미는 대신 여백을 준다 — 줄이 접혀도 계층이 유지된다.
        paddingLeft: block.indent ? `${block.indent * INDENT_STEP}px` : undefined,
      }}
    >
      <Spans spans={block.spans} />
    </p>
  );
}

/** 링크 글자의 꼴. 웹·앱·편집 면이 같은 파랑을 쓴다. */
const LINK_STYLE: CSSProperties = { color: '#1d4ed8', textDecoration: 'underline', wordBreak: 'break-all' };

function Spans({ spans }: { spans: InlineSpan[] }) {
  return (
    <>
      {spans.map((span, index): ReactNode => {
        const inner = styledSpan(span, index);
        if (!span.href) return inner;
        // 새 창으로 연다 — 공지를 읽던 자리를 잃지 않게. 청첩장·부고장은 대개 바깥 주소다.
        return (
          <a key={index} href={span.href} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>
            {inner}
          </a>
        );
      })}
    </>
  );
}

function styledSpan(span: InlineSpan, index: number): ReactNode {
  // 꾸밈이 하나도 없으면 태그를 씌우지 않는다 — 옛 글이 span 수백 개로 감싸일 이유가 없다.
  if (!span.bold && !span.italic && !span.underline && !span.color && !span.highlight && !span.size) return span.text;

  return (
    <span
      key={index}
      style={{
        // 문단의 `line-height: 1.7` 은 배수라 큰 글씨도 제 줄높이를 갖는다 — 겹치지 않는다.
        fontSize: span.size ? `${span.size}px` : undefined,
        fontWeight: span.bold ? 700 : undefined,
        fontStyle: span.italic ? 'italic' : undefined,
        textDecoration: span.underline ? 'underline' : undefined,
        // 색을 따로 고른 글자는 그 색이 이긴다. 안 골랐는데 형광펜이 어두우면 흰색으로 뒤집는다 —
        // 안 그러면 남색 형광펜 위의 본문색 글자가 통째로 묻힌다.
        color: span.color ?? (span.highlight && isDarkColor(span.highlight) ? '#ffffff' : undefined),
        background: span.highlight,
        // 형광펜은 글자에 딱 붙으면 읽기 힘들다. 배경이 있을 때만 좌우를 조금 띄운다.
        padding: span.highlight ? '0 2px' : undefined,
        borderRadius: span.highlight ? '3px' : undefined,
      }}
    >
      {span.text}
    </span>
  );
}
